import { useEffect, useRef } from 'react';
import { useFetcher } from 'react-router';
import type { ProductTileViewModel } from '@rocco/ui/tile-product/view-models/product';
import type { DisplayMode } from '@rocco/types/enum/display-mode';
import { CategoryPageRecentlyViewed } from '~/ui-rocco/pages/category-page/CategoryPageRecentlyViewed';
import {
    RECENTLY_VIEWED_ACTION,
    type loader,
} from '~/routes/actions.recently-viewed';

interface RecentlyViewedWrapperProps {
    displayMode: DisplayMode;
    /**
     * Applied to whichever block renders. Spacing belongs here rather than on a
     * wrapper in the page: the block is fetched client-side and renders nothing
     * when the reader has no history, and a margin held outside it would still
     * open its gap with nothing in it.
     */
    className?: string;
}

/**
 * Wrapper component that lazily fetches recently viewed products client-side.
 * This removes the recently viewed fetch from the critical path of the server loader,
 * improving initial page load time (TTFB).
 *
 * Renders the shared CategoryPageRecentlyViewed strip — the same one the
 * professionals and projects directories use — so the three directories carry
 * one recently-viewed pattern, standard tile actions included.
 */
export const RecentlyViewedWrapper = ({
    displayMode,
    className,
}: RecentlyViewedWrapperProps) => {
    const fetcher = useFetcher<typeof loader>({
        key: 'recently-viewed-products',
    });
    const hasFetched = useRef(false);

    useEffect(() => {
        if (fetcher.state === 'idle' && !hasFetched.current) {
            fetcher.load(`${RECENTLY_VIEWED_ACTION}?type=Products`);
            hasFetched.current = true;
        }
    }, [fetcher]);

    const products: ProductTileViewModel[] =
        fetcher.data?.recentlyViewedProducts ?? [];

    // Don't render anything while loading or if no products
    if (fetcher.state === 'loading' || products.length === 0) {
        return null;
    }

    return (
        <div className={className}>
            <CategoryPageRecentlyViewed
                displayMode={displayMode}
                items={products}
            />
        </div>
    );
};
